import { useFeatureUsage } from '@/hooks/useUsageLimits';
import { FeatureKey } from '@/types/subscription';
import { useLanguage } from '@/contexts';

interface UsageBadgeProps {
  featureKey: FeatureKey;
  showLabel?: boolean;
  mockData?: {
    current_usage: number;
    limit_value: number | null;
    percentage_used: number | null;
    isUnlimited: boolean;
  };
}

export function UsageBadge({ featureKey, showLabel = true, mockData }: UsageBadgeProps) {
  const fetchedUsage = useFeatureUsage(featureKey);
  const { t } = useLanguage();
  
  // Use mock data if provided, otherwise use fetched data
  const usage = mockData || fetchedUsage;

  if (!usage) {
    return <div className="skeleton h-6 w-20 bg-gray-200 animate-pulse rounded" />;
  }

  const isUnlimited = usage.limit_value === null;
  const percentage = usage.percentage_used || 0;

  // Color logic: green <75%, yellow 75-90%, red >90%
  const getColorClasses = () => {
    if (isUnlimited) {
      return {
        bg: 'bg-blue-50 dark:bg-blue-900/30',
        border: 'border-blue-200 dark:border-blue-700',
        text: 'text-blue-700 dark:text-blue-300',
        label: 'text-blue-600 dark:text-blue-400',
        indicator: 'bg-blue-500 dark:bg-blue-400'
      };
    }
    if (percentage >= 90) {
      return {
        bg: 'bg-red-50 dark:bg-red-900/30',
        border: 'border-red-200 dark:border-red-700',
        text: 'text-red-700 dark:text-red-300',
        label: 'text-red-600 dark:text-red-400',
        indicator: 'bg-red-500 dark:bg-red-400'
      };
    }
    if (percentage >= 75) {
      return {
        bg: 'bg-yellow-50 dark:bg-yellow-900/30',
        border: 'border-yellow-200 dark:border-yellow-700',
        text: 'text-yellow-700 dark:text-yellow-300',
        label: 'text-yellow-600 dark:text-yellow-400',
        indicator: 'bg-yellow-500 dark:bg-yellow-400'
      };
    }
    return {
      bg: 'bg-green-50 dark:bg-green-900/30',
      border: 'border-green-200 dark:border-green-700',
      text: 'text-green-700 dark:text-green-300',
      label: 'text-green-600 dark:text-green-400',
      indicator: 'bg-green-500 dark:bg-green-400'
    };
  };

  const colors = getColorClasses();

  // Format feature key for display using i18n
  const formatFeatureKey = (key: FeatureKey): string => {
    return t(`usageWidget.features.${key}`);
  };

  return (
    <div className={`group relative flex items-center justify-between p-3 rounded-lg border ${colors.border} ${colors.bg} transition-all duration-200 hover:shadow-md dark:hover:shadow-lg`}>
      {/* Status indicator dot */}
      <div className="flex items-center gap-3 flex-1">
        <div className={`w-2 h-2 rounded-full ${colors.indicator} animate-pulse`}></div>
        
        <div className="flex-1 min-w-0">
          {showLabel && (
            <div className={`text-xs font-medium uppercase tracking-wide ${colors.label} mb-0.5 transition-colors duration-300`}>
              {formatFeatureKey(featureKey)}
            </div>
          )}
          <div className={`text-sm font-semibold ${colors.text} transition-colors duration-300`}>
            {isUnlimited ? (
              <span className="flex items-center gap-1">
                <span>{t('usageWidget.status.unlimited')}</span>
                <span className="text-lg">∞</span>
              </span>
            ) : (
              t('usageWidget.status.limited')
                .replace('{{current}}', String(usage.current_usage))
                .replace('{{limit}}', String(usage.limit_value))
            )}
          </div>
        </div>
      </div>

      {/* Percentage indicator */}
      {!isUnlimited && (
        <div className={`text-xs font-bold ${colors.text} ml-2 transition-colors duration-300`}>
          {t('usageWidget.status.percentage').replace('{{percentage}}', String(percentage))}
        </div>
      )}
      
      {/* Progress bar at bottom */}
      {!isUnlimited && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-200 dark:bg-slate-700 rounded-b-lg overflow-hidden transition-colors duration-300">
          <div 
            className={`h-full ${colors.indicator} transition-all duration-500`}
            style={{ width: `${Math.min(percentage, 100)}%` }}
          ></div>
        </div>
      )}
    </div>
  );
}

